import { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';

const TEAM = ['Adam', 'Ray', 'Diana', 'Bob'];
const SHIFT_OPTS = ['OFF', 'PTO', 'TM'];

const NHL_TEAMS = [
  'ANA','ARI','BOS','BUF','CGY','CAR','CHI','COL','CBJ','DAL',
  'DET','EDM','FLA','LAK','MIN','MTL','NSH','NJD','NYI','NYR',
  'OTT','PHI','PIT','SJS','SEA','STL','TBL','TOR','UTA','VAN','VGK','WSH','WPG',
];
const MLB_TEAMS = [
  'ARI','ATL','BAL','BOS','CHC','CWS','CIN','CLE','COL','DET',
  'HOU','KC','LAA','LAD','MIA','MIL','MIN','NYM','NYY','OAK',
  'PHI','PIT','SD','SF','SEA','STL','TB','TEX','TOR','WSH',
];

const today = () => new Date().toISOString().slice(0, 10);

function pad(n) { return String(n).padStart(2, '0'); }

// ── Main CalendarTab ───────────────────────────────────────────────────────

export function CalendarTab({ currentUser, newActionRef }) {
  const [view, setView] = useState('schedule');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEvent, setEditEvent] = useState(null); // null | 'new' | event obj
  const [focusDate, setFocusDate] = useState(today());
  const [monthOffset, setMonthOffset] = useState(0); // months from current

  // Date range to fetch: current month ± some buffer
  const fetchRange = () => {
    const base = new Date();
    base.setDate(1);
    const from = new Date(base);
    from.setMonth(from.getMonth() - 1);
    const to = new Date(base);
    to.setMonth(to.getMonth() + 3);
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const { from, to } = fetchRange();
      const data = await api.getCalendar(from, to);
      setEvents(data.events || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Expose new event via ref
  useEffect(() => {
    if (newActionRef) newActionRef.current = () => setEditEvent('new');
  }, [newActionRef]);

  const openNew = (date) => setEditEvent({ _new: true, date: date || today() });
  const openEdit = (ev) => setEditEvent(ev);

  const onSaved = () => { setEditEvent(null); load(); };

  const viewBtns = [
    { id: 'schedule', label: '≡ Schedule' },
    { id: 'month',    label: '▦ Month' },
  ];

  return (
    <div style={{ paddingTop: 12 }}>
      {/* View switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {viewBtns.map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={{
            background: view === v.id ? '#0f172a' : '#e2e8f0',
            color: view === v.id ? '#fff' : '#475569',
            border: 'none', borderRadius: 8, padding: '7px 14px',
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}>{v.label}</button>
        ))}
        <button onClick={() => openNew()} style={{ marginLeft: 'auto', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          + Add Game
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 48 }}>Loading calendar…</div>
      ) : view === 'schedule' ? (
        <ScheduleView events={events} currentUser={currentUser} onEdit={openEdit} onNew={openNew} />
      ) : (
        <MonthView events={events} currentUser={currentUser} onEdit={openEdit} onNew={openNew} monthOffset={monthOffset} setMonthOffset={setMonthOffset} />
      )}

      {editEvent && (
        <EventModal
          event={editEvent === 'new' ? { _new: true, date: today() } : editEvent}
          currentUser={currentUser}
          onSaved={onSaved}
          onClose={() => setEditEvent(null)}
        />
      )}
    </div>
  );
}

// ── Schedule View ─────────────────────────────────────────────────────────

function ScheduleView({ events, currentUser, onEdit, onNew }) {
  if (events.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>No games scheduled yet</div>
        <button onClick={() => onNew()} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
          Add First Game
        </button>
      </div>
    );
  }

  // Group by month
  const byMonth = {};
  for (const ev of events) {
    const [y, m] = ev.date.split('-');
    const key = `${y}-${m}`;
    if (!byMonth[key]) byMonth[key] = [];
    byMonth[key].push(ev);
  }
  const months = Object.keys(byMonth).sort();
  const todayStr = today();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {months.map(mon => {
        const [y, m] = mon.split('-');
        const label = new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
        return (
          <div key={mon}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, padding: '16px 0 6px', borderBottom: '2px solid #e2e8f0', marginBottom: 8 }}>{label}</div>
            {byMonth[mon].map(ev => (
              <ScheduleRow key={ev.id} ev={ev} currentUser={currentUser} onEdit={onEdit} isToday={ev.date === todayStr} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function ScheduleRow({ ev, currentUser, onEdit, isToday }) {
  const d = new Date(ev.date + 'T12:00:00');
  const dayNum = d.getDate();
  const dayName = d.toLocaleString('default', { weekday: 'short' });
  const isBoth = ev.team === 'both';
  const isPens = ev.team === 'pens' || isBoth;
  const isBucs = ev.team === 'bucs' || isBoth;

  const myShifts = (ev.shifts || []).filter(s => s.person === currentUser);

  return (
    <div
      onClick={() => onEdit(ev)}
      style={{
        display: 'flex', gap: 12, padding: '10px 12px',
        marginBottom: 6, borderRadius: 10, cursor: 'pointer',
        background: isToday ? '#eff6ff' : '#fff',
        border: `1px solid ${isToday ? '#bfdbfe' : '#e2e8f0'}`,
        boxShadow: '0 1px 3px rgba(0,0,0,.04)',
        transition: 'background .1s',
      }}
    >
      {/* Date blob */}
      <div style={{ width: 44, flexShrink: 0, textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{dayName}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: isToday ? '#2563eb' : '#0f172a', lineHeight: 1.1 }}>{dayNum}</div>
      </div>

      {/* Game info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isBoth ? (
          <>
            <GameLine slot={1} ev={ev} />
            <GameLine slot={2} ev={ev} />
          </>
        ) : (
          <GameLine slot={1} ev={ev} />
        )}
        {ev.day_note ? <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontStyle: 'italic' }}>{ev.day_note}</div> : null}
      </div>

      {/* My shift badge */}
      {myShifts.length > 0 && (
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end' }}>
          {myShifts.map((s, i) => (
            <span key={i} style={{ fontSize: 11, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>{s.shift || '—'}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function GameLine({ slot, ev }) {
  const opp = slot === 1 ? ev.game1_opponent : ev.game2_opponent;
  const time = slot === 1 ? ev.game1_time : ev.game2_time;
  const loc = slot === 1 ? ev.game1_location : ev.game2_location;
  const bcast = slot === 1 ? ev.game1_broadcaster : ev.game2_broadcaster;

  if (!opp && !time) return null;

  const isBoth = ev.team === 'both';
  const teamForSlot = isBoth ? (slot === 1 ? 'bucs' : 'pens') : ev.team;

  const teamColors = { pens: { bg: '#fef9c3', text: '#92400e', dot: '#eab308' }, bucs: { bg: '#dcfce7', text: '#15803d', dot: '#22c55e' } };
  const tc = teamColors[teamForSlot] || { bg: '#f1f5f9', text: '#475569', dot: '#94a3b8' };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: tc.dot, flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{opp || '—'}</span>
      {time && <span style={{ fontSize: 12, color: '#64748b' }}>{time}</span>}
      {loc && <LocBadge loc={loc} />}
      {bcast && <span style={{ fontSize: 11, color: '#64748b' }}>{bcast}</span>}
    </div>
  );
}

function LocBadge({ loc }) {
  const map = {
    home:  { label: 'HOME',  bg: '#dcfce7', color: '#15803d' },
    cloud: { label: 'CLOUD', bg: '#dbeafe', color: '#1d4ed8' },
    away:  { label: 'AWAY',  bg: '#fef3c7', color: '#92400e' },
  };
  const key = (loc || '').toLowerCase();
  const s = map[key] || { label: loc.toUpperCase(), bg: '#f1f5f9', color: '#475569' };
  return <span style={{ fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, borderRadius: 4, padding: '2px 5px', letterSpacing: .3 }}>{s.label}</span>;
}

// ── Month View ─────────────────────────────────────────────────────────────

function MonthView({ events, currentUser, onEdit, onNew, monthOffset, setMonthOffset }) {
  const base = new Date();
  base.setDate(1);
  base.setMonth(base.getMonth() + monthOffset);
  const year = base.getFullYear();
  const month = base.getMonth();
  const label = base.toLocaleString('default', { month: 'long', year: 'numeric' });

  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build event map by date string
  const eventMap = {};
  for (const ev of events) {
    if (!eventMap[ev.date]) eventMap[ev.date] = [];
    eventMap[ev.date].push(ev);
  }

  const todayStr = today();
  const cells = [];
  // Leading blanks
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = `${year}-${pad(month + 1)}-${pad(d)}`;
    cells.push({ d, ds, evs: eventMap[ds] || [] });
  }
  // Pad to complete rows
  while (cells.length % 7 !== 0) cells.push(null);

  const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  return (
    <div>
      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={() => setMonthOffset(o => o - 1)} style={navBtn}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{label}</span>
        <button onClick={() => setMonthOffset(o => o + 1)} style={navBtn}>›</button>
      </div>

      {/* DOW headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
        {DOW.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#94a3b8', padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      {/* Cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((cell, i) => {
          if (!cell) return <div key={`blank-${i}`} style={{ minHeight: 64, background: '#f8fafc', borderRadius: 8 }} />;
          const { d, ds, evs } = cell;
          const isToday = ds === todayStr;
          const hasPens = evs.some(e => e.team === 'pens' || e.team === 'both');
          const hasBucs = evs.some(e => e.team === 'bucs' || e.team === 'both');

          return (
            <div
              key={ds}
              onClick={() => evs.length > 0 ? onEdit(evs[0]) : onNew(ds)}
              style={{
                minHeight: 64, background: isToday ? '#eff6ff' : '#fff',
                border: `1px solid ${isToday ? '#bfdbfe' : '#e2e8f0'}`,
                borderRadius: 8, padding: '4px 5px', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 2,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: isToday ? 800 : 500, color: isToday ? '#2563eb' : '#475569', lineHeight: 1 }}>{d}</div>
              {evs.map(ev => <MonthEventChip key={ev.id} ev={ev} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthEventChip({ ev }) {
  const isBoth = ev.team === 'both';
  const isPens = ev.team === 'pens';
  const bg = isBoth ? 'linear-gradient(90deg, #dcfce7 50%, #fef9c3 50%)' : isPens ? '#fef9c3' : '#dcfce7';
  const opp = ev.game1_opponent;
  return (
    <div style={{
      fontSize: 10, fontWeight: 600, borderRadius: 4, padding: '2px 4px',
      background: bg, color: '#0f172a',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      lineHeight: 1.4,
    }}>
      {opp || (isPens ? 'PIT' : 'PGH')}
    </div>
  );
}

const navBtn = { background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 18, cursor: 'pointer', color: '#0f172a' };

// ── Event Form Modal ───────────────────────────────────────────────────────

function EventModal({ event, currentUser, onSaved, onClose }) {
  const isNew = !!event._new;

  const [date, setDate] = useState(event.date || today());
  const [team, setTeam] = useState(event.team || 'pens');
  const [dayNote, setDayNote] = useState(event.day_note || '');

  // Game 1
  const [g1opp, setG1opp] = useState(event.game1_opponent || '');
  const [g1time, setG1time] = useState(event.game1_time || '');
  const [g1loc, setG1loc] = useState(event.game1_location || '');
  const [g1bcast, setG1bcast] = useState(event.game1_broadcaster || '');
  const [g1bcast2, setG1bcast2] = useState(''); // simulcast slot
  const [g1simulcast, setG1simulcast] = useState(event.game1_broadcaster?.includes(' & ') || false);

  // Game 2 (crossover)
  const [g2opp, setG2opp] = useState(event.game2_opponent || '');
  const [g2time, setG2time] = useState(event.game2_time || '');
  const [g2loc, setG2loc] = useState(event.game2_location || '');
  const [g2bcast, setG2bcast] = useState(event.game2_broadcaster || '');
  const [g2simulcast, setG2simulcast] = useState(event.game2_broadcaster?.includes(' & ') || false);
  const [g2bcast2, setG2bcast2] = useState('');

  // Shifts: { person: { slot: shiftString } }
  const initShifts = () => {
    const s = {};
    for (const p of TEAM) {
      s[p] = { 1: '', 2: '' };
    }
    for (const sh of (event.shifts || [])) {
      if (!s[sh.person]) s[sh.person] = { 1: '', 2: '' };
      s[sh.person][sh.game_slot || 1] = sh.shift || '';
    }
    return s;
  };
  const [shifts, setShifts] = useState(initShifts);

  const [saving, setSaving] = useState(false);

  const setShift = (person, slot, val) => {
    setShifts(s => ({ ...s, [person]: { ...s[person], [slot]: val } }));
  };

  const isBoth = team === 'both';
  const oppList = (isBoth || team === 'pens') ? NHL_TEAMS : (team === 'bucs') ? MLB_TEAMS : [];

  const save = async () => {
    setSaving(true);
    const id = event.id || `ev_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

    const bcast1 = g1simulcast && g1bcast2 ? `${g1bcast} & ${g1bcast2}` : g1bcast;
    const bcast2 = g2simulcast && g2bcast2 ? `${g2bcast} & ${g2bcast2}` : g2bcast;

    const ev = {
      id, date, team,
      game1_opponent: g1opp, game1_time: g1time, game1_location: g1loc, game1_broadcaster: bcast1,
      game2_opponent: g2opp, game2_time: g2time, game2_location: g2loc, game2_broadcaster: bcast2,
      day_note: dayNote,
      created_by: event.created_by || currentUser,
    };

    const shiftRows = [];
    for (const person of TEAM) {
      const s1 = shifts[person]?.[1];
      const s2 = shifts[person]?.[2];
      if (s1) shiftRows.push({ id: `sh_${id}_${person}_1`, event_id: id, person, game_slot: 1, shift: s1 });
      if (isBoth && s2) shiftRows.push({ id: `sh_${id}_${person}_2`, event_id: id, person, game_slot: 2, shift: s2 });
    }

    try {
      await api.saveCalendarEvent(ev, shiftRows);
      onSaved();
    } catch (e) {
      alert('Save failed. Please try again.');
    }
    setSaving(false);
  };

  const del = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this game entry?')) return;
    await api.deleteCalendarEvent(event.id);
    onSaved();
  };

  // Quick-select broadcaster chips
  const bcastChips = ['SNP', 'ESPN', 'ESPN2', 'TNT', 'ABC', 'NHLN', 'MLB.tv'];

  const BcastField = ({ val, setVal, val2, setVal2, simul, setSimul }) => (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        {bcastChips.map(c => (
          <span key={c} onClick={() => setVal(v => v === c ? '' : c)}
            style={{ ...chipStyle2, background: val === c ? '#0f172a' : '#f1f5f9', color: val === c ? '#fff' : '#334155' }}
          >{c}</span>
        ))}
        <span onClick={() => setSimul(s => !s)}
          style={{ ...chipStyle2, background: simul ? '#7c3aed' : '#f1f5f9', color: simul ? '#fff' : '#334155' }}
        >+ Simulcast</span>
      </div>
      <input value={val} onChange={e => setVal(e.target.value)} placeholder="Broadcaster (free text)" style={inp} />
      {simul && <input value={val2} onChange={e => setVal2(e.target.value)} placeholder="Second broadcaster" style={{ ...inp, marginTop: 4 }} />}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '16px 12px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520, marginBottom: 32, boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}>
        {/* Header */}
        <div style={{ background: '#0f172a', color: '#fff', padding: '14px 18px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{isNew ? 'Add Game' : 'Edit Game'}</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Date */}
          <div>
            <div style={lbl}>Date</div>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
          </div>

          {/* Team */}
          <div>
            <div style={lbl}>Team</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{id:'pens',label:'🐧 Penguins'},{id:'bucs',label:'⚾ Pirates'},{id:'both',label:'🔀 Both'}].map(t => (
                <span key={t.id} onClick={() => setTeam(t.id)}
                  style={{ ...chipStyle2, background: team === t.id ? '#0f172a' : '#f1f5f9', color: team === t.id ? '#fff' : '#334155', flex: 1, textAlign: 'center' }}
                >{t.label}</span>
              ))}
            </div>
          </div>

          {/* Game 1 */}
          <GameSection
            slot={1}
            team={isBoth ? 'bucs' : team}
            label={isBoth ? 'Pirates Game' : 'Game'}
            opp={g1opp} setOpp={setG1opp}
            time={g1time} setTime={setG1time}
            loc={g1loc} setLoc={setG1loc}
          />
          <div>
            <div style={lbl}>Broadcaster (Game {isBoth ? '1 – Pirates' : ''})</div>
            <BcastField val={g1bcast} setVal={setG1bcast} val2={g1bcast2} setVal2={setG1bcast2} simul={g1simulcast} setSimul={setG1simulcast} />
          </div>

          {/* Game 2 — crossover only */}
          {isBoth && (
            <>
              <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: 12 }}>
                <GameSection
                  slot={2}
                  team="pens"
                  label="Penguins Game"
                  opp={g2opp} setOpp={setG2opp}
                  time={g2time} setTime={setG2time}
                  loc={g2loc} setLoc={setG2loc}
                />
                <div style={{ marginTop: 12 }}>
                  <div style={lbl}>Broadcaster (Game 2 – Penguins)</div>
                  <BcastField val={g2bcast} setVal={setG2bcast} val2={g2bcast2} setVal2={setG2bcast2} simul={g2simulcast} setSimul={setG2simulcast} />
                </div>
              </div>
            </>
          )}

          {/* Shifts */}
          <div>
            <div style={{ ...lbl, marginBottom: 10 }}>Engineer Shifts</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {TEAM.map(person => (
                <div key={person} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ width: 52, fontSize: 13, fontWeight: 600, color: '#334155' }}>{person}</div>
                  <ShiftInput
                    value={shifts[person]?.[1] || ''}
                    onChange={v => setShift(person, 1, v)}
                    label={isBoth ? 'PIT' : ''}
                  />
                  {isBoth && (
                    <ShiftInput
                      value={shifts[person]?.[2] || ''}
                      onChange={v => setShift(person, 2, v)}
                      label="PGH"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Day note */}
          <div>
            <div style={lbl}>Day Note (optional)</div>
            <input value={dayNote} onChange={e => setDayNote(e.target.value)} placeholder="Altcast, Bucs BP, studio show, etc." style={inp} />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4 }}>
            {!isNew ? (
              <button onClick={del} style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Delete</button>
            ) : <div />}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#334155' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: saving ? .6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GameSection({ slot, team, label, opp, setOpp, time, setTime, loc, setLoc }) {
  const oppList = team === 'bucs' ? MLB_TEAMS : NHL_TEAMS;
  return (
    <div>
      <div style={lbl}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Opponent */}
        <div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Opponent</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {oppList.map(t => (
              <span key={t} onClick={() => setOpp(o => o === t ? '' : t)}
                style={{ ...chipStyle2, background: opp === t ? '#0f172a' : '#f1f5f9', color: opp === t ? '#fff' : '#334155', padding: '3px 8px', fontSize: 11 }}
              >{t}</span>
            ))}
          </div>
          <input value={opp} onChange={e => setOpp(e.target.value)} placeholder="or type opponent" style={inp} />
        </div>

        {/* Time + Location */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Game Time</div>
            <input value={time} onChange={e => setTime(e.target.value)} placeholder="e.g. 7:00 PM" style={inp} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Location</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {['home','cloud','away'].map(l => (
                <span key={l} onClick={() => setLoc(x => x === l ? '' : l)}
                  style={{ ...chipStyle2, background: loc === l ? '#0f172a' : '#f1f5f9', color: loc === l ? '#fff' : '#334155', flex: 1, textAlign: 'center', fontSize: 11 }}
                >{l}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShiftInput({ value, onChange, label }) {
  const [custom, setCustom] = useState(!SHIFT_OPTS.includes(value) && value !== '');
  const [raw, setRaw] = useState(value);

  useEffect(() => {
    if (!SHIFT_OPTS.includes(value) && value !== '') {
      setCustom(true);
      setRaw(value);
    }
  }, [value]);

  return (
    <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
      {label && <span style={{ fontSize: 10, color: '#94a3b8', width: 22, flexShrink: 0 }}>{label}</span>}
      {custom ? (
        <input
          value={raw}
          onChange={e => { setRaw(e.target.value); onChange(e.target.value); }}
          onBlur={() => { if (!raw) { setCustom(false); onChange(''); } }}
          placeholder="e.g. 12:00 PM"
          style={{ ...inp, flex: 1, padding: '5px 8px', fontSize: 12 }}
          autoFocus
        />
      ) : (
        <div style={{ display: 'flex', gap: 3, flex: 1 }}>
          {SHIFT_OPTS.map(o => (
            <span key={o} onClick={() => onChange(o === value ? '' : o)}
              style={{ ...chipStyle2, flex: 1, textAlign: 'center', fontSize: 11, background: value === o ? '#0f172a' : '#f1f5f9', color: value === o ? '#fff' : '#334155' }}
            >{o}</span>
          ))}
          <span onClick={() => setCustom(true)}
            style={{ ...chipStyle2, flex: 1.2, textAlign: 'center', fontSize: 11, background: '#e0f2fe', color: '#0369a1' }}
          >Time…</span>
        </div>
      )}
    </div>
  );
}

const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .5 };
const inp = { border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
const chipStyle2 = { display: 'inline-block', padding: '5px 10px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', userSelect: 'none' };
